import React, { useCallback, useEffect, useState } from 'react';
import { Clock, FileText, Calendar, FileSignature, Eye, Bell, Check, Loader2, BookOpen } from 'lucide-react';
import { apiClient } from '../../api/client';

/**
 * IRTimelinePanel — Sprint 174 P0.
 *
 * Per-investor chronological "story strip" rendered inside the investor
 * card. Aggregates notes + calendar + contract dispatches + tab telemetry
 * + diligence file events + prospect alerts into one DESC-sorted feed.
 *
 * The button is collapsed by default to keep the investor grid tight;
 * expanding lazy-loads /api/vault/investors/{id}/ir-timeline.
 */

const PALETTE = {
  cream: '#FAFAED',
  plum: '#2A2235',
  muted: '#5C526A',
  coral: '#F99B7D',
  mint: '#A3E2C9',
  lavender: '#9BB8ED',
  border: 'rgba(92,82,106,0.10)',
};

const KIND_META = {
  note:            { Icon: FileText,      bg: '#F0F4FB', fg: '#3F4D7A', label: 'Note' },
  calendar:        { Icon: Calendar,      bg: '#FFF6EE', fg: '#9A4A33', label: 'Calendar' },
  contract:        { Icon: FileSignature, bg: '#F2E4E4', fg: '#7A4040', label: 'Contract' },
  'contract-signed': { Icon: Check,      bg: '#E8F6EF', fg: '#1F5C44', label: 'Signed' },
  telemetry:       { Icon: Eye,           bg: '#E9E4F2', fg: '#5C526A', label: 'Vault visit' },
  diligence:       { Icon: BookOpen,      bg: '#FFF9F2', fg: '#9A4A33', label: 'Diligence' },
  'prospect-alert':{ Icon: Bell,          bg: '#F2E4E4', fg: '#7A4040', label: 'Inbound' },
};

const fmtTs = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    if (diff < 1) return 'just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    if (diff < 4320) return `${Math.floor(diff / 1440)}d ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch { return '—'; }
};

export const IRTimelinePanel = ({ investorId }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!investorId) return;
    setLoading(true);
    try { setData(await apiClient.request(`/api/vault/investors/${investorId}/ir-timeline?window_days=90`)); }
    catch { /* swallow */ }
    setLoading(false);
  }, [investorId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && data === null) load();
  };

  return (
    <div className="mt-1.5" data-testid={`ir-timeline-${investorId}`}>
      <button
        type="button"
        onClick={toggle}
        className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold transition-transform hover:scale-[1.02]"
        style={{ background: PALETTE.lavender + '22', color: '#3F4D7A', border: `1px solid ${PALETTE.lavender}66` }}
        data-testid={`ir-timeline-toggle-${investorId}`}
      >
        <Clock size={11} /> IR Timeline {data?.total_events ? `· ${data.total_events}` : ''} {open ? '▾' : '▸'}
      </button>
      {open && (
        <div className="mt-2 rounded-lg p-2.5" style={{ background: PALETTE.cream, border: `1px solid ${PALETTE.border}` }} data-testid={`ir-timeline-panel-${investorId}`}>
          {loading && (
            <div className="text-xs flex items-center gap-1.5" style={{ color: PALETTE.muted }} data-testid={`ir-timeline-loading-${investorId}`}>
              <Loader2 size={10} className="animate-spin" /> Building 90-day story strip…
            </div>
          )}
          {!loading && data && data.total_events === 0 && (
            <p className="text-xs" style={{ color: PALETTE.muted }} data-testid={`ir-timeline-empty-${investorId}`}>
              No activity yet in the last 90 days.
            </p>
          )}
          {!loading && data && data.total_events > 0 && (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.12em] mb-1.5" style={{ color: PALETTE.muted }}>
                Last 90 days · {data.total_events} event{data.total_events === 1 ? '' : 's'}
              </div>
              <ol className="space-y-1.5 max-h-72 overflow-y-auto pr-1" data-testid={`ir-timeline-events-${investorId}`}>
                {data.events.map((ev, idx) => {
                  const meta = KIND_META[ev.kind] || { Icon: FileText, bg: '#fff', fg: PALETTE.muted, label: ev.kind };
                  const { Icon } = meta;
                  return (
                    <li key={idx} className="flex items-start gap-2" data-testid={`ir-timeline-event-${investorId}-${idx}`}>
                      <span className="w-6 h-6 flex-shrink-0 rounded-full flex items-center justify-center mt-0.5" style={{ background: meta.bg, color: meta.fg }}>
                        <Icon size={11} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-bold truncate" style={{ color: PALETTE.plum }}>{ev.headline}</span>
                          <span className="text-[10px] flex-shrink-0" style={{ color: PALETTE.muted }}>{fmtTs(ev.at)}</span>
                        </div>
                        {ev.detail && <p className="text-[10px] truncate" style={{ color: PALETTE.muted }}>{ev.detail}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IRTimelinePanel;
